import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { useDbContext } from "../../../../context/DbContext";
import {
  FiBriefcase,
  FiMapPin,
  FiMail,
  FiPhone,
  FiHash,
  FiCalendar,
  FiDollarSign,
  FiEdit3,
  FiSave,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiDatabase,
  FiUser,
  FiPrinter,
  FiRefreshCw,
  FiLock,
} from "react-icons/fi";

const CompanySettings = forwardRef(function CompanySettings({ onClose }, ref) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const customerCode = localStorage.getItem("customerCode") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: "", text: "" });

  const [formData, setFormData] = useState({
    AUTO: null,
    Code: "",
    DBName: "",
    CustomerCode: "",
    name: "",
    Sname: "",
    PrintName: "",
    CType: "",
    add1: "",
    add2: "",
    add3: "",
    add4: "",
    add5: "",
    pin: "",
    telephone: "",
    mobile: "",
    email: "",
    tin: "",
    cst: "",
    kgst: "",
    DLNO: "",
    currencytype: "",
    s_currency: "",
    secondfont: "",
    sdate: "",
    edate: "",
    printertype: "",
    PrintModel: "",
  });

  const [originalData, setOriginalData] = useState({});

  const fetchCompanyDetails = async () => {
    if (!dbCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setStatusMsg({ type: "", text: "" });
    try {
      const url = `${apiBaseUrl}/main/companySettings/${dbCode}${
        customerCode ? `?customerCode=${encodeURIComponent(customerCode)}` : ""
      }`;
      const res = await axios.get(url);
      if (res.data && res.data.success && res.data.data) {
        const item = res.data.data;
        const initialForm = {
          AUTO: item.AUTO ?? null,
          Code: item.Code ?? dbCode,
          DBName: item.DBName ?? "",
          CustomerCode: item.CustomerCode ?? customerCode,
          name: (item.name || "").trim(),
          Sname: (item.Sname || "").trim(),
          PrintName: (item.PrintName || "").trim(),
          CType: (item.CType || "").trim(),
          add1: (item.add1 || "").trim(),
          add2: (item.add2 || "").trim(),
          add3: (item.add3 || "").trim(),
          add4: (item.add4 || "").trim(),
          add5: (item.add5 || "").trim(),
          pin: (item.pin || "").trim(),
          telephone: (item.telephone || "").trim(),
          mobile: (item.mobile || "").trim(),
          email: (item.email || "").trim(),
          tin: (item.tin || "").trim(),
          cst: (item.cst || "").trim(),
          kgst: (item.kgst || "").trim(),
          DLNO: (item.DLNO || "").trim(),
          currencytype: (item.currencytype || "INR").trim(),
          s_currency: (item.s_currency || "Paise").trim(),
          secondfont: (item.secondfont || "Inter").trim(),
          sdate: item.sdateYmd || (item.sdate ? item.sdate.split("T")[0] : ""),
          edate: item.edateYmd || (item.edate ? item.edate.split("T")[0] : ""),
          printertype: (item.printertype || "").trim(),
          PrintModel: (item.PrintModel || "").trim(),
        };
        setFormData(initialForm);
        setOriginalData(initialForm);
      }
    } catch (err) {
      console.error("Error fetching company details:", err);
      setStatusMsg({
        type: "error",
        text: "Could not load company details from GoldCompany DB.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, [apiBaseUrl, dbCode]);

  const handleChange = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
    setStatusMsg({ type: "", text: "" });
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!dbCode) return;
    setSaving(true);
    setStatusMsg({ type: "", text: "" });

    try {
      // Code, DBName, and CustomerCode uniquely identify the company row to update
      const payload = {
        ...formData,
        Code: formData.Code || dbCode,
        DBName: formData.DBName || "",
        CustomerCode: originalData.CustomerCode || formData.CustomerCode || customerCode,
        email: originalData.email || formData.email, // preserve existing email
      };

      const res = await axios.post(
        `${apiBaseUrl}/main/companySettings/${dbCode}`,
        payload
      );

      if (res.data && res.data.success) {
        if (res.data.result && res.data.result.rowsAffected === 0) {
          const errMsg = `No matching record updated for company code: ${dbCode}.`;
          setStatusMsg({
            type: "error",
            text: errMsg,
          });
          alert(errMsg);
        } else {
          const successMsg = "Company details updated successfully!";
          setStatusMsg({
            type: "success",
            text: "Company details updated successfully in GoldCompany!",
          });
          setOriginalData(formData);
          setIsEditing(false);
          alert(successMsg);
        }
      } else {
        const failMsg = res.data?.message || "Failed to update company details.";
        setStatusMsg({
          type: "error",
          text: failMsg,
        });
        alert(failMsg);
      }
    } catch (err) {
      console.error("Error saving company details:", err);
      const serverErr =
        err.response?.data?.message ||
        "Network or server error updating company details.";
      setStatusMsg({
        type: "error",
        text: serverErr,
      });
      alert(serverErr);
    } finally {
      setSaving(false);
    }
  };

  // Expose methods to parent SoftWareSettings
  useImperativeHandle(ref, () => ({
    handleSave,
    fetchCompanyDetails,
    startEdit: () => setIsEditing(true),
    cancelEdit: handleCancel,
  }));

  if (loading) {
    return (
      <div className="w-full bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center justify-center space-y-3 font-['Inter',sans-serif]">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs font-semibold text-slate-600 font-['Inter',sans-serif]">
          Retrieving company details from GoldCompany...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 font-['Inter',sans-serif]">
      {/* ── Top Header Toolbar / Status Bar ── */}
      <div className="bg-white rounded-lg border border-slate-200 p-2.5 px-3.5 shadow-xs flex flex-wrap items-center justify-between gap-2 font-['Inter',sans-serif]">
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-['Inter',sans-serif]">
          <div className="flex items-center space-x-1.5 px-2 py-1 bg-blue-50 border border-blue-100 rounded-md text-blue-800 font-semibold font-['Inter',sans-serif]">
            <FiDatabase className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span>DB Code:</span>
            <span className="font-mono font-bold text-blue-900">
              {formData.Code || dbCode || "N/A"}
            </span>
          </div>

          {formData.DBName && (
            <div className="flex items-center space-x-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-md text-slate-700 font-['Inter',sans-serif]">
              <span className="text-slate-400">Database:</span>
              <span className="font-mono font-semibold text-slate-800">
                {formData.DBName}
              </span>
            </div>
          )}

          {formData.CustomerCode && (
            <div className="flex items-center space-x-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-md text-slate-700 font-['Inter',sans-serif]">
              <FiUser className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-400">Customer Code:</span>
              <span className="font-medium text-slate-800">
                {formData.CustomerCode}
              </span>
              <span className="ml-1 text-[9px] font-bold text-slate-400 bg-slate-200/60 px-1 py-0.2 rounded flex items-center gap-0.5">
                <FiLock className="w-2.5 h-2.5" /> Fixed
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={fetchCompanyDetails}
                className="inline-flex items-center space-x-1 px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition cursor-pointer border-0 font-['Inter',sans-serif]"
                title="Refresh from GoldCompany"
              >
                <FiRefreshCw className="w-3 h-3 text-slate-600" />
                <span>Refresh</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(true);
                  setStatusMsg({ type: "", text: "" });
                }}
                className="inline-flex items-center space-x-1.5 px-3 py-1 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition cursor-pointer shadow-xs border-0 font-['Inter',sans-serif]"
              >
                <FiEdit3 className="w-3.5 h-3.5" />
                <span>Edit Company</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center space-x-1 px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition cursor-pointer border-0 disabled:opacity-50 font-['Inter',sans-serif]"
              >
                <FiX className="w-3.5 h-3.5 text-slate-600" />
                <span>Cancel</span>
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center space-x-1.5 px-3.5 py-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition cursor-pointer shadow-xs border-0 disabled:opacity-60 font-['Inter',sans-serif]"
              >
                {saving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FiSave className="w-3.5 h-3.5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Status Banner ── */}
      {statusMsg.text && (
        <div
          className={`flex items-center space-x-2 p-2.5 px-3 rounded-lg text-xs font-semibold ${
            statusMsg.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-red-50 text-red-800 border border-red-200"
          } font-['Inter',sans-serif]`}
        >
          {statusMsg.type === "success" ? (
            <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <FiAlertCircle className="w-4 h-4 text-red-600 shrink-0" />
          )}
          <span>{statusMsg.text}</span>
        </div>
      )}

      {/* ── 2x2 Grid Form Container ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 font-['Inter',sans-serif]">
        {/* ── CARD 1: Entity & Legal Profile ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-xs space-y-2.5 font-['Inter',sans-serif]">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center space-x-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              <h2 className="font-bold text-slate-800 text-[11.5px] uppercase tracking-wider font-['Inter',sans-serif]">
                Entity &amp; Legal Profile
              </h2>
            </div>
            {isEditing && (
              <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.2 rounded border border-blue-200">
                Editing Enabled
              </span>
            )}
          </div>

          <div className="space-y-2 text-[11px] font-['Inter',sans-serif]">
            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Registered Company / Firm Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.name}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11.5px] font-bold rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="e.g. SherGold Jewellery HQ"
                />
                <FiBriefcase className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Short Name / Brand Display
                </label>
                <input
                  type="text"
                  value={formData.Sname}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("Sname", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] font-semibold rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="e.g. SherGold"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Bill / Receipt Print Header
                </label>
                <input
                  type="text"
                  value={formData.PrintName}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("PrintName", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="e.g. SHERGOLD JEWELLERS"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Company Business Classification
              </label>
              <input
                type="text"
                value={formData.CType}
                disabled={true}
                className="w-full px-2.5 py-1 text-[11px] bg-slate-100 border border-slate-200 rounded-md text-slate-600 font-semibold cursor-not-allowed font-['Inter',sans-serif]"
                title="Classification managed via core licensing"
              />
            </div>
          </div>
        </div>

        {/* ── CARD 2: Contact & Communication ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-xs space-y-2.5 font-['Inter',sans-serif]">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center space-x-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-600" />
              <h2 className="font-bold text-slate-800 text-[11.5px] uppercase tracking-wider font-['Inter',sans-serif]">
                Official Contact &amp; Channels
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-['Inter',sans-serif]">
            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Mobile Phone
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.mobile}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-medium rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="+91 9847000000"
                />
                <FiPhone className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Landline / Phone
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.telephone}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("telephone", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-medium rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="0495 2720000"
                />
                <FiPhone className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            {/* Email Field: STRICTLY LOCKED / IMMUTABLE */}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-0.5">
                <label className="block font-semibold text-slate-700 text-[11px] font-['Inter',sans-serif]">
                  Registered Account Email
                </label>
                <span className="text-[9.5px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded flex items-center gap-0.5">
                  <FiLock className="w-2.5 h-2.5" /> Read-Only
                </span>
              </div>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  disabled={true}
                  readOnly={true}
                  className="w-full pl-7 pr-2.5 py-1 text-[11px] font-medium rounded-md bg-slate-100 border border-slate-200 text-slate-600 cursor-not-allowed font-['Inter',sans-serif]"
                  title="Registered account email cannot be modified"
                />
                <FiMail className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Receipt / Font Name
              </label>
              <input
                type="text"
                value={formData.secondfont}
                disabled={!isEditing}
                onChange={(e) => handleChange("secondfont", e.target.value)}
                className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                  isEditing
                    ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                    : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                }`}
                placeholder="Inter / Arial"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Printer Hardware Model
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.PrintModel}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("PrintModel", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="e.g. TM-T82"
                />
                <FiPrinter className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 3: Registered Physical Address ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-xs space-y-2.5 font-['Inter',sans-serif]">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center space-x-1.5">
              <span className="w-2 h-2 rounded-full bg-indigo-600" />
              <h2 className="font-bold text-slate-800 text-[11.5px] uppercase tracking-wider font-['Inter',sans-serif]">
                Registered Physical Address
              </h2>
            </div>
          </div>

          <div className="space-y-2 text-[11px] font-['Inter',sans-serif]">
            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Address Line 1 (Building / Premises)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.add1}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("add1", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="Building, Door No."
                />
                <FiMapPin className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Address Line 2 (Street / Road)
                </label>
                <input
                  type="text"
                  value={formData.add2}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("add2", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="Street / Main Road"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  City / Town (Line 3)
                </label>
                <input
                  type="text"
                  value={formData.add3}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("add3", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="City / Area"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  State (Line 4)
                </label>
                <input
                  type="text"
                  value={formData.add4}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("add4", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="Kerala"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Country (Line 5)
                </label>
                <input
                  type="text"
                  value={formData.add5}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("add5", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="India"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                  Postal PIN Code
                </label>
                <input
                  type="text"
                  value={formData.pin}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("pin", e.target.value)}
                  className={`w-full px-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="673001"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 4: Taxation, Legal & Financial Calendar ── */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-xs space-y-2.5 font-['Inter',sans-serif]">
          <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
            <div className="flex items-center space-x-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-600" />
              <h2 className="font-bold text-slate-800 text-[11.5px] uppercase tracking-wider font-['Inter',sans-serif]">
                Taxation, Licenses &amp; Period
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-['Inter',sans-serif]">
            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                GSTIN / TIN Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.tin}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("tin", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-mono font-bold rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="32AAAAA0000A1Z5"
                />
                <FiHash className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Trade / License No (DLNO)
              </label>
              <input
                type="text"
                value={formData.DLNO}
                disabled={!isEditing}
                onChange={(e) => handleChange("DLNO", e.target.value)}
                className={`w-full px-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                  isEditing
                    ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                    : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                }`}
                placeholder="LIC-12345/2025"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                CST Number
              </label>
              <input
                type="text"
                value={formData.cst}
                disabled={!isEditing}
                onChange={(e) => handleChange("cst", e.target.value)}
                className={`w-full px-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                  isEditing
                    ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                    : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                }`}
                placeholder="CST-00000"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                KGST / State Tax No.
              </label>
              <input
                type="text"
                value={formData.kgst}
                disabled={!isEditing}
                onChange={(e) => handleChange("kgst", e.target.value)}
                className={`w-full px-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                  isEditing
                    ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                    : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                }`}
                placeholder="KGST-00000"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                FY Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.sdate}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("sdate", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                />
                <FiCalendar className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                FY End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.edate}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("edate", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-mono rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                />
                <FiCalendar className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Currency Unit
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.currencytype}
                  disabled={!isEditing}
                  onChange={(e) => handleChange("currencytype", e.target.value)}
                  className={`w-full pl-7 pr-2.5 py-1 text-[11px] font-semibold rounded-md transition outline-none font-['Inter',sans-serif] ${
                    isEditing
                      ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                      : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                  }`}
                  placeholder="INR / ₹"
                />
                <FiDollarSign className="absolute left-2 top-2 text-slate-400 text-[11px]" />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-0.5 text-[11px] font-['Inter',sans-serif]">
                Sub-Currency (Fraction)
              </label>
              <input
                type="text"
                value={formData.s_currency}
                disabled={!isEditing}
                onChange={(e) => handleChange("s_currency", e.target.value)}
                className={`w-full px-2.5 py-1 text-[11px] rounded-md transition outline-none font-['Inter',sans-serif] ${
                  isEditing
                    ? "bg-white border border-blue-400 ring-1 ring-blue-400 text-slate-900"
                    : "bg-slate-50 border border-slate-200 text-slate-800 cursor-not-allowed"
                }`}
                placeholder="Paise / Cents"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CompanySettings;
